import { FormFieldsGeneratorService } from './../form-helpers/form-fields-generator.service';
import { AfterContentInit, Component, Input, OnInit } from '@angular/core';
import {
  NotificationProviderService,
  NotificationProviderTypeEnum
} from '../../external-interfaces/notification-provider.service';
import { FormStateChangeService } from '../form-state/form-state-change.service';
import { FormGroup } from '@angular/forms';
import { CommonModalService } from '../../utils/modals/common-modal.service';
import { IdentityConfirmationTypeEnum } from '../../models/cartDetails/identityConfirmationType.enum';
import { CommonInfoService } from '../../cart-process/common-info.service';
import { TranslateService } from '@ngx-translate/core';
import { FormSectionTypeEnum, FormStepsService } from '../form-steps/form-steps.service';
import { CommonModalTemplatesEnum } from '../../utils/modals/common-modal-templates.enum';
import { DataLayerService } from '../../customer-actions/data-layer.service';
import { IConfig } from '../../interfaces/config.interface';
import { Observable } from 'rxjs';

@Component({
  selector: 'user-authentication-mode-component',
  templateUrl: './user-authentication.component.html',
})
export class UserAuthenticationComponent implements OnInit, AfterContentInit {
  @Input() offlineIdentityConfirmationEnabled: boolean;
  @Input() onlineIdentityConfirmationEnabled: boolean;
  @Input() MIDIdentityConfirmationEnabled: boolean;
  @Input() isPosAvailable: boolean;

  public checkText: string;
  public themeResourcePath = window['themeResourcePath'];
  public identityConfirmationType: string;
  public offlineIdentityConfirmationTitle: string;
  public offlineIdentityConfirmationDescription: string;
  public isSelectedOfflineIdentityConfirmation: boolean;
  public isSelectedOnlineIdentityConfirmation: boolean;
  public isSelectedMIDIdentityConfirmation: boolean;
  public isValidateIdentityConfirmation: boolean;
  public formGroups: { [s: string]: FormGroup } = {};
  public formSectionTypeEnum: typeof FormSectionTypeEnum = FormSectionTypeEnum;
  public deliveryCostInformation = 'identityConfirmation.deliveryCostInformation';
  public isVirginBrand = false;
  public enableMIdCard: boolean;
  public activeForm: FormGroup;

  constructor(
    public formStepsService: FormStepsService,
    public translate: TranslateService,
    private notificationProvider: NotificationProviderService,
    private formStateChangeService: FormStateChangeService,
    private commonModalService: CommonModalService,
    private commonInfoService: CommonInfoService,
    private dataLayerService: DataLayerService,
    private formsGenerator: FormFieldsGeneratorService
  ) {
    this.formsGenerator.watchActiveForm().subscribe(form => {
      this.activeForm = form;
    });
  }

  ngOnInit(): void {
    this.prepareFormControls();
    this.setupNotificationSubscriptions();
    this.loadTranslations();
  }

  ngAfterContentInit(): void {
    this.formStepsService.setSectionVisibility(FormSectionTypeEnum.USER_AUTHENTICATION);
  }

  public async changeIdentityConfirmationType(identityConfirmationType: string): Promise<void> {
    try {
      await this.openNextSection(identityConfirmationType);
    } catch (err) {
      return;
    }

    this.identityConfirmationType = identityConfirmationType;
    this.isSelectedOfflineIdentityConfirmation = identityConfirmationType === IdentityConfirmationTypeEnum.OFFLINE;
    this.isSelectedOnlineIdentityConfirmation = identityConfirmationType === IdentityConfirmationTypeEnum.ONLINE;
    this.isSelectedMIDIdentityConfirmation = identityConfirmationType === IdentityConfirmationTypeEnum.M_OBYWATEL;

    this.activeForm.controls.identityConfirmationType.setValue(identityConfirmationType);

    this.formStateChangeService.setField({
      inputField: this.formStateChangeService.StateInputFields.IDENTITY_CONFIRMATION_TYPE,
      value: this.identityConfirmationType
    });

    this.getIdentityConfirmationTitle(identityConfirmationType)
      .subscribe(title => {
        this.dataLayerService.setDeliveryPageEvent(title, 'add_identity_verification_info');
      });
  }

  private getIdentityConfirmationTitle(identityConfirmationType: string): Observable<string> {
    const typeMap: Record<string, string> = {
      [IdentityConfirmationTypeEnum.ONLINE]: 'online',
      [IdentityConfirmationTypeEnum.M_OBYWATEL]: 'mId',
      [IdentityConfirmationTypeEnum.OFFLINE]: 'offline',
    };

    const type = typeMap[identityConfirmationType] || 'offline';
    return this.translate.get(`identityConfirmation.${type}IdentityConfirmationTitle`);
  }

  public openModal(type: string): void {
    const modalData = {
      templateName: CommonModalTemplatesEnum[type === 'MID'
        ? 'SHOW_MORE_M_ID_AUTHENTICATION'
        : 'SHOW_MORE_ONLINE_AUTHENTICATION']
    };

    this.commonModalService.setData(modalData);
  }

  private prepareFormControls(): void {
    this.formsGenerator.prepareIdentityConfirmation(this.identityConfirmationType);
    this.activeForm = this.formsGenerator.activeForm;
  }

  private setupNotificationSubscriptions(): void {
    this.notificationProvider.subscribe(NotificationProviderTypeEnum.COMMON_INFO, (commonInfo: CommonInfoService) => {
      if (commonInfo) {
        this.prepareDisclaimerText();
        this.isVirginBrand = commonInfo.isVirginBrand;
      }
    });

    this.notificationProvider.subscribe(NotificationProviderTypeEnum.CONFIG, (config: IConfig) => {
      if (config) {
        this.enableMIdCard = config.enableMIdCard;
      }
    });
  }

  private loadTranslations(): void {
    this.translate.get('identityConfirmation.selectedOption').subscribe((text) => {
      this.checkText = text;
    });
  }

  private prepareDisclaimerText(): void {
    if (this.commonInfoService.isRetentionOffer) {
      this.offlineIdentityConfirmationTitle = 'identityConfirmation.offlineIdentityConfirmationTitle';
      this.offlineIdentityConfirmationDescription = 'identityConfirmation.offlineRetentionIdentityConfirmationDescription';
    } else {
      this.offlineIdentityConfirmationTitle = 'identityConfirmation.offlineIdentityConfirmationTitle';
      this.offlineIdentityConfirmationDescription = 'identityConfirmation.offlineIdentityConfirmationDescription';
    }
  }

  private openNextSection(identityConfirmationType: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.formStepsService.getSection(FormSectionTypeEnum.USER_AUTHENTICATION).dataChanged) {
        this.formStepsService.openModalConfirmation('authentication').then(() => {

          this.formStateChangeService.setField({
            inputField: this.formStateChangeService.StateInputFields.DELIVERY_MODE_NAME,
            value: ''
          });

          this.formStateChangeService.setField({
            inputField: this.formStateChangeService.StateInputFields.DELIVERY_OPTION_VALUE_NAME,
            value: ''
          });

          this.formStateChangeService.setField({
            inputField: this.formStateChangeService.StateInputFields.PAYMENT_MODE_VALUE_NAME,
            value: ''
          });

          this.formStateChangeService.setField({
            inputField: this.formStateChangeService.StateInputFields.SELECTED_POS,
            value: false
          });

          this.activeForm.get('paymentMode').setValue('');
          this.activeForm.get('identityConfirmationType').setValue(identityConfirmationType);

          if (this.activeForm.controls.shippingAddress) {
            this.activeForm.get('shippingAddress').setValue('');
          }
          if (this.activeForm.controls.shippingType) {
            this.activeForm.get('shippingType').setValue('');
          }
          if (this.activeForm.controls.selectedPos) {
            this.activeForm.removeControl('selectedPos');
          }

          this.activeForm.updateValueAndValidity();

          this.formStepsService.getSection(FormSectionTypeEnum.DELIVERY).dataChanged = false;
          this.formStepsService.getSection(FormSectionTypeEnum.PAYMENT).dataChanged = false;
          this.formStepsService.getSection(FormSectionTypeEnum.SUMMARY).isOpen = false;

          resolve();
        }).catch(() => reject());
      } else {
        this.formStepsService.openNextSection(FormSectionTypeEnum.USER_AUTHENTICATION);
        resolve();
      }
    });
  }
}


